import * as THREE from 'three';

import { Axial, axialKey, axialToWorld, distance } from '../map/HexCoordinates';
import { HexCellData, cornerElevation, waterLevel } from '../map/HexSurface';
import { CORNERS, HEX_SIZE, elevationToY } from '../map/HexMetrics';
import { deckY, walkY } from '../map/RoadSurface';
import { crossingSeconds } from '../map/Movement';
import type { PropModel } from '../props/PropModels';
import { Glide } from '../map/Glide';

/**
 * Where everybody is standing.
 *
 * A pawn per player: **the knight** (`PawnModel.ts`), the same for everybody
 * and without a player colour — who is who says the name above it, and a flag
 * is planned for that. Until the model has loaded, or if it will not, a pawn is
 * the old six-sided cone in the player's colour, the way a building without a
 * file stays a silhouette from code.
 *
 * **It faces where it is walking.** Every step turns it towards the cell it is
 * heading for, and a pawn that stops keeps looking the way it went.
 *
 * Steps arrive one cooldown apart, and a pawn that teleported every quarter of
 * a second would read as stuttering rather than as walking, so a move is
 * interpolated over the gap. This is presentation only — the server's position
 * is the one that counts, and the pawn is always on its way to it.
 *
 * Heights come from the terrain the client currently holds. A player can be
 * standing on ground that has not streamed in yet: rather than guess, the pawn
 * keeps its last height until the chunk arrives, and `refresh` re-reads them
 * all when it does.
 */

/**
 * How a pawn moves: at a **constant speed** towards the last cell it was said
 * to be on, not by animating each step over its own quarter of a second.
 *
 * The two look the same when the steps arrive exactly on time. They never do —
 * one comes 30 ms late, the next comes with the one behind it — and a per-step
 * animation shows every one of those as a pause and a lurch, because it always
 * finishes early and then waits. Moving at a fixed rate towards wherever the
 * server last said, the pawn simply keeps walking; jitter turns into being a
 * few centimetres further back, which nobody can see.
 *
 * The speed is one cell per server cooldown, so a pawn covers ground at exactly
 * the rate the rules allow.
 */


/**
 * Najkrótszy czas przejścia między polami.
 *
 * Podłoga na wypadek terenu, o którym klient jeszcze nic nie wie: krok liczony
 * na zero trwałby zero i pionek by skoczył.
 */
const SHORTEST_STEP = 0.05;

/**
 * Beyond this many cells a move is not a step and is not animated.
 *
 * Walking arrives one cell at a time, so anything further came from somewhere
 * else — an admin moving somebody, or a client that missed a run of steps while
 * the socket was down. Sliding a pawn across half a continent would read as
 * flight; appearing there reads as what it is.
 */
const SNAP_DISTANCE = 3;

/** Own pawn, in a colour no name hash produces. */
const SELF_COLOR = 0xffd166;

/**
 * Pionek: wysokość i promień podstawy.
 *
 * **Dwa zmniejszenia, każde z innego powodu.** Najpierw o szóstą część, żeby
 * sześcioro zmieściło się na polu — pole ma sześć stanowisk, a stożki
 * przenikałyby się na pierścieniu. Potem **o połowę**, razem z przejściem na
 * model rycerza: postać ma wyglądać jak człowiek przy budynkach, a nie jak
 * pionek wielkości chaty. Rachunek stanowisk niżej liczony jest dla dawnej,
 * szerszej podstawy, więc przy mniejszej zostaje z zapasem.
 */
const RADIUS = (HEX_SIZE * 0.3167) / 2;
const HEIGHT = HEX_SIZE / 2;

/** Wysokość pionka w jednostkach świata — dla etykiet nad nim. */
export const PAWN_HEIGHT = HEIGHT;

/**
 * O ile obrócić model, żeby patrzył w kierunku marszu.
 *
 * **Rycerz patrzy wzdłuż −X.** Przy zerze chodził bokiem, przy −90° tyłem —
 * zgadywanie znaku z sylwetki (dalej w stronę −X, co wyglądało na plecy)
 * okazało się błędne: to, co sięga dalej, jest z przodu. Sprawdzone w grze.
 */
const MODEL_FACING = +Math.PI / 2;

/** Wysokość drzewca sztandaru przy postaci — trochę ponad jej głowę. */
const STAFF_HEIGHT = HEIGHT * 1.6;

/**
 * Gdzie stoi drzewce, gdy rycerz nie ma ustawionego punktu `banner`: przy boku,
 * w przestrzeni znormalizowanego modelu (wysokość jeden).
 */
const DEFAULT_BANNER_AT: [number, number, number] = [0, 0, 0.35];

/**
 * Skąd wziąć sztandar gracza: gotowy obiekt drzewca z płatem, podstawą
 * w początku układu, albo `null`, gdy go nie ma.
 */
export type BannerSource = (
  player: string,
  version: number,
  height: number
) => Promise<THREE.Object3D | null>;

/**
 * Jak daleko od środka pola stoi pionek, gdy nie jest sam.
 *
 * **Z rachunku, nie z oka.** Sześć podstaw o średnicy `2 * RADIUS` (0,317
 * jednostki świata) musi zmieścić się na obwodzie: `2π * r ≥ 6 * 0,317` daje
 * `r ≥ 0,303`. Sześć dziesiątych promienia heksa to 0,30 — podstawy stykają
 * się, ale stożek zwęża się ku górze, więc widać sześć osobnych postaci
 * stojących ciasno, a nie jedną plamę. Dalej od środka nie warto: przy 0,433
 * zaczyna się krawędź pola i pionek wystawałby na sąsiada.
 */
const SLOT_RING = HEX_SIZE * 0.6;

/**
 * Ułamek drogi od środka pola do środka krawędzi, na którym stoi pionek.
 *
 * Wprost z `SLOT_RING`: środek krawędzi leży w `HEX_SIZE * cos 30°`, więc to
 * jest ta sama odległość wyrażona tak, jak liczy się po niej wysokość terenu —
 * a wysokość na wachlarzu trójkątów jest liniowa od środka do krawędzi.
 */
const SLOT_REACH = SLOT_RING / (HEX_SIZE * Math.cos(Math.PI / 6));

/** Jeden wektor na wszystkie obroty w klatce — styczna liczy się co pionek. */
const FACING = new THREE.Vector3();

interface Marker {
  username: string;
  cell: Axial;
  /** Które z sześciu stanowisk pola zajmuje — z serwera, nie z rachunku tutaj. */
  slot: number;
  /** Środek pionka na wysokości połowy postaci — tak stoi też stożek. */
  mesh: THREE.Object3D;
  /** Kolor własny stożka; model go nie ma. */
  color: number;
  /** Ciało pionka — rycerz albo stożek — osobno od sztandaru obok niego. */
  body: THREE.Object3D;
  /** Wersja sztandaru, o którą chodzi; pusty, gdy gracz go nie ma. */
  banner?: number;
  /** Postawiony sztandar, gdy już przyjechał. */
  flag?: THREE.Object3D;
  /** Where the server last said they are, in world units. */
  target: THREE.Vector3;
  /** Skąd zaczęło się to przejście — punkt, nie prędkość. */
  from: THREE.Vector3;
  /**
   * Sunięcie po całej trasie, gdy ją znamy — patrz `Glide`.
   *
   * **Tylko własny pionek**, bo tylko o swojej trasie klient wie z góry: cudze
   * kroki przychodzą po jednym i nie ma z czego przewidywać. Nieobecne znaczy
   * „idź od wiadomości do wiadomości", czyli tak, jak chodziło wszystko przed
   * tą zmianą.
   */
  glide?: Glide;
  /**
   * Ile ostatni tor miał trwać i ile trwał — **zostaje po jego zgaszeniu**.
   *
   * Pomiar z serwera przychodzi po dojściu, czyli wtedy, gdy toru już nie ma;
   * bez tego nie byłoby czego z nim zestawić.
   */
  travel?: { planned: number; took: number };
  /** Punkt sterujący łuku tego kroku — patrz `bendOf`. */
  control: THREE.Vector3;
  /** Kierunek, którym pionek wszedł na to pole; zerowy przed pierwszym krokiem. */
  enter: THREE.Vector3;
  /** Ile sekund trwa to przejście: dokładnie tyle, ile trwa krok na serwerze. */
  duration: number;
  /** Ile z niego minęło. */
  elapsed: number;
  /**
   * Pionek ma zniknąć **po dojściu**, a nie w tej chwili.
   *
   * Ta sama rzecz, co przy wozach: dla serwera zejście z mapy jest chwilą — kto
   * położył się spać, jest już pod dachem — a dla patrzącego ostatnie przejście
   * dopiero się wtedy zaczyna. Skasowanie znacznika od razu gasiło postać
   * **przed** wejściem do osady, jakby rozpłynęła się w drodze.
   */
  leaving: boolean;
}

/**
 * Jak mocno zaokrągla się zakręt, w ułamku długości kroku.
 *
 * Punkt sterujący leży **na kierunku, którym się tu przyszło**, w tej części
 * odległości do następnego pola. Zero daje łamaną, jedynka — łuk wychodzący poza
 * pole; połowa jest tym, co czyta się jak skręt, a nie jak obrót w miejscu.
 */
const BEND = 0.5;

/**
 * Punkt na łuku kroku — jedna funkcja dla pionków i dla statków.
 *
 * **Krzywa Béziera drugiego stopnia, bez znajomości następnego pola.** Trasa
 * rysuje się krzywą Catmull-Rom przez środki pól i przechodzi przez nie
 * dokładnie; tutaj jest to samo, tylko liczone krok po kroku, bo o następnym
 * polu wiadomo dopiero wtedy, gdy serwer je poda. Punktem sterującym jest
 * kierunek **wejścia** na to pole, więc każdy krok wychodzi tam, gdzie poprzedni
 * przyszedł — i to jest cała gładkość: kierunek zmienia się ciągle, zamiast
 * przeskakiwać o 60° na każdym środku pola.
 *
 * Na prostej punkt sterujący leży na odcinku, więc łuk jest odcinkiem — ruch po
 * linii prostej nie faluje.
 */
export function alongStep(
  from: THREE.Vector3,
  control: THREE.Vector3,
  to: THREE.Vector3,
  part: number,
  out: THREE.Vector3
): THREE.Vector3 {
  const rest = 1 - part;
  return out
    .copy(from)
    .multiplyScalar(rest * rest)
    .addScaledVector(control, 2 * rest * part)
    .addScaledVector(to, part * part);
}

/**
 * Dokąd łuk jest zwrócony w tym punkcie — pochodna krzywej, znormalizowana.
 *
 * Stąd bierze się obrót bryły: patrzy tam, gdzie naprawdę płynie w tej chwili,
 * a nie tam, gdzie leży środek następnego pola.
 */
export function headingOnStep(
  from: THREE.Vector3,
  control: THREE.Vector3,
  to: THREE.Vector3,
  part: number,
  out: THREE.Vector3
): THREE.Vector3 {
  const rest = 1 - part;
  return out
    .set(0, 0, 0)
    .addScaledVector(control, 2 * rest)
    .addScaledVector(from, -2 * rest)
    .addScaledVector(to, 2 * part)
    .addScaledVector(control, -2 * part);
}

/**
 * Punkt sterujący kroku: na kierunku wejścia, w `BEND` odległości do celu.
 *
 * `enter` zerowy — czyli pierwszy krok, po którym nie było poprzedniego —
 * daje punkt na odcinku, więc krok jest prosty.
 */
export function bendOf(
  from: THREE.Vector3,
  to: THREE.Vector3,
  enter: THREE.Vector3,
  out: THREE.Vector3
): THREE.Vector3 {
  const span = from.distanceTo(to);
  if (span < 1e-6 || enter.lengthSq() < 1e-6) {
    return out.copy(from).lerp(to, BEND);
  }
  return out.copy(from).addScaledVector(enter, span * BEND);
}

/**
 * The colour of a name.
 *
 * Exported because a player's buildings are drawn in the same colour as their
 * pawn — a second hash would mean the same person in two different colours.
 */
export function colorFor(username: string): number {
  let hash = 0;
  for (const character of username) {
    hash = (hash * 31 + character.codePointAt(0)!) >>> 0;
  }

  // Saturated and light enough to stay legible against grass, sand and snow.
  return new THREE.Color().setHSL((hash % 360) / 360, 0.65, 0.6).getHex();
}

export class PlayerMarkers {
  readonly object = new THREE.Group();

  private readonly markers = new Map<string, Marker>();
  private readonly geometry = new THREE.ConeGeometry(RADIUS, HEIGHT, 6);
  /** Rycerz, gdy już przyjechał. */
  private model?: PropModel;
  private bannerSource?: BannerSource;
  /** Punkt `banner` rycerza, w przestrzeni znormalizowanego modelu. */
  private bannerAt: [number, number, number] = DEFAULT_BANNER_AT;

  constructor(
    private readonly world: Map<number, HexCellData>,
    private readonly self: string
  ) {
    this.object.name = 'players';
  }

  /**
   * Ground height at a cell, or `null` while its chunk is still on its way.
   *
   * Keyed with `axialKey`, which is the cell's own identity. `centerKey` from
   * `HexSurface` looks like the right function and is not: it identifies the
   * *vertex* at a cell's centre, on the doubled lattice the corners live on.
   * Asking the streamer's map with it misses every time, and a miss here is
   * silent — the pawn simply keeps a height, and every player stands at sea
   * level on top of the terrain.
   */
  private groundY(cell: Axial): number | null {
    const data = this.world.get(axialKey(cell.q, cell.r));
    if (!data) return null;
    // Na moście pokład, na wodzie lustro, a na lądzie wysokość pola — ta sama
    // kolejność pytań, co w `walkY`. Woda wchodzi tu jednym przypadkiem:
    // **wsiadaniem na statek**, gdzie ostatni krok prowadzi na pole wody. Bez
    // tego pionek nurkował na dno akurat w chwili, w której miał wejść na
    // pokład.
    return deckY(data) ?? elevationToY(waterLevel(data) ?? data.elevation);
  }

  /**
   * Gdzie stanąć: w środku pola albo na swoim stanowisku.
   *
   * **Sam stoi w środku.** Dopóki nikogo obok nie ma, pionek stoi tam, gdzie
   * wskazał kursor — pole zaznacza się w środku i postać stojąca z boku pustego
   * heksa wyglądałaby na przesuniętą. Drugi wchodzący rozkłada obu na ich
   * stanowiska, a ostatni, który zostanie, wraca na środek.
   *
   * **Wysokość liczona z nachylenia, a nie z pola.** Powierzchnia to wachlarz
   * trójkątów od środka do narożników, więc punkt na osi środek → środek
   * krawędzi leży między wysokością środka a średnią dwóch narożników tej
   * krawędzi — liniowo, bo trójkąt jest płaski. Bez tego pionek na zboczu stałby
   * na poziomie środka pola: jeden but w powietrzu, drugi w ziemi.
   */
  private place(cell: Axial, slot: number, alone: boolean, fallback: number): THREE.Vector3 {
    const { x, z } = axialToWorld(cell.q, cell.r);
    const data = this.world.get(axialKey(cell.q, cell.r));
    const deck = deckY(data);
    // **Pokład, lustro, dopiero potem grunt** — ta sama kolejność, co w `walkY`
    // i `groundY`, i to było tu jedyne miejsce, które jej nie miało. Pole wody
    // niesie **dno**, a woda jest nieprzezroczysta: pionek postawiony tu na
    // wysokości pola po prostu **znikał pod powierzchnią**. Widać to było jako
    // „nie ma ludzika" po zejściu na ląd — a wystarczyło, żeby jakakolwiek droga
    // postawiła go choćby na chwilę na wodzie.
    const ground = data ? (deck ?? elevationToY(waterLevel(data) ?? data.elevation)) : fallback;

    // The cone's origin is its middle, so half its height puts its point up and
    // its base on the ground.
    //
    // **Na moście stoi się w środku, choćby było tłoczno** — stanowiska leżą na
    // pierścieniu, a pokład jest wąski: rozsunięci staliby na wodzie.
    if (alone || !data || deck !== null) {
      return new THREE.Vector3(x, ground + HEIGHT / 2, z);
    }

    const near = CORNERS[slot % 6];
    const far = CORNERS[(slot + 1) % 6];
    const toEdgeX = (near.x + far.x) / 2;
    const toEdgeZ = (near.z + far.z) / 2;

    const corners =
      (cornerElevation(this.world, cell, slot % 6) +
        cornerElevation(this.world, cell, (slot + 1) % 6)) /
      2;
    const height = data.elevation * (1 - SLOT_REACH) + corners * SLOT_REACH;

    return new THREE.Vector3(
      x + toEdgeX * SLOT_REACH,
      elevationToY(height) + HEIGHT / 2,
      z + toEdgeZ * SLOT_REACH
    );
  }

  /** Kto stoi na tym polu — z własnym pionkiem włącznie, bo on też zajmuje miejsce. */
  private standingOn(cell: Axial): Marker[] {
    return [...this.markers.values()].filter(
      (marker) =>
        !marker.leaving && marker.cell.q === cell.q && marker.cell.r === cell.r
    );
  }

  /**
   * Przelicz, gdzie stoją wszyscy na tym polu, i puść ich tam **przejściem**.
   *
   * Wołane, ilekroć skład pola się zmienia: wejście drugiej osoby zsuwa obie ze
   * środka na stanowiska, a wyjście przedostatniej oddaje środek tej, która
   * została. Idzie tą samą drogą, co krok — `from`, `target`, czas przejścia —
   * bo to jest ten sam rodzaj ruchu, tylko krótszy; przeskok w miejscu czytałby
   * się jak błąd rysowania.
   */
  private restack(cell: Axial): void {
    const here = this.standingOn(cell);
    const alone = here.length <= 1;

    for (const marker of here) {
      const target = this.place(marker.cell, marker.slot, alone, marker.mesh.position.y - HEIGHT / 2);
      if (marker.target.distanceToSquared(target) < 1e-8) continue;

      marker.from.copy(marker.mesh.position);
      marker.target.copy(target);
      marker.duration = Math.max(
        SHORTEST_STEP,
        crossingSeconds(this.world.get(axialKey(cell.q, cell.r)))
      );
      marker.elapsed = 0;
    }
  }

  /**
   * Model przyjechał: wszyscy, którzy już stoją, zamieniają stożek na rycerza.
   */
  useModel(model: PropModel): void {
    this.model = model;
    for (const marker of this.markers.values()) {
      this.release(marker.body);
      marker.mesh.remove(marker.body);
      marker.body = this.body(marker.color);
      marker.mesh.add(marker.body);
    }
  }

  /**
   * Skąd brać sztandary i gdzie rycerz trzyma drzewce. Wołane raz, gdy gra ma
   * już punkty modeli; pionki, które stoją, dostają swoje sztandary od razu.
   */
  useBanners(source: BannerSource, at?: [number, number, number]): void {
    this.bannerSource = source;
    if (at) this.bannerAt = at;
    for (const [id, marker] of this.markers) this.raise(id, marker);
  }

  /** Gracz ma sztandar w tej wersji — albo nie ma go wcale. */
  setBanner(playerId: string, version: number | undefined): void {
    const marker = this.markers.get(playerId);
    if (!marker || marker.banner === version) return;
    marker.banner = version;
    this.raise(playerId, marker);
  }

  /** Postaw sztandar, o który chodzi pionkowi — albo zdejmij ten, który stoi. */
  private raise(playerId: string, marker: Marker): void {
    if (marker.flag) {
      marker.mesh.remove(marker.flag);
      marker.flag = undefined;
    }
    const version = marker.banner;
    if (version === undefined || !this.bannerSource) return;

    void this.bannerSource(playerId, version, STAFF_HEIGHT).then((flag) => {
      // Między pytaniem a odpowiedzią pionek mógł zniknąć albo zmienić sztandar.
      if (!flag || this.markers.get(playerId) !== marker || marker.banner !== version) return;
      if (marker.flag) marker.mesh.remove(marker.flag);
      const [x, y, z] = this.bannerAt;
      // Punkt jest w przestrzeni rycerza: wysokość jeden, podstawa na zerze —
      // a środek pionka leży w połowie jego wysokości.
      flag.position.set(x * HEIGHT, y * HEIGHT - HEIGHT / 2, z * HEIGHT);
      // Płat powiewa wzdłuż +X, czyli za plecami rycerza, który patrzy wzdłuż −X.
      marker.mesh.add(flag);
      marker.flag = flag;
    });
  }

  /**
   * Ciało pionka, zaczepione tak, żeby jego środek wypadał w początku układu —
   * cała reszta tego pliku liczy pozycję pionka jako środek postaci.
   */
  private body(color: number): THREE.Object3D {
    if (!this.model) {
      const cone = new THREE.Mesh(this.geometry, new THREE.MeshLambertMaterial({ color }));
      cone.castShadow = true;
      return cone;
    }

    const knight = new THREE.Group();
    for (const part of this.model.parts) {
      // Geometria i materiał wspólne dla wszystkich — rycerz jest jeden.
      const mesh = new THREE.Mesh(part.geometry, part.material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      knight.add(mesh);
    }
    // `flatten` stawia model na zerze i daje mu jednostkową wysokość.
    knight.scale.setScalar(HEIGHT);
    knight.position.y = -HEIGHT / 2;
    return knight;
  }

  /** Zwolnij to, co ciało pionka ma na własność: dziś tylko materiał stożka. */
  private release(holder: THREE.Object3D): void {
    holder.traverse((node) => {
      if (node instanceof THREE.Mesh && node.geometry === this.geometry) {
        (node.material as THREE.Material).dispose();
      }
    });
  }

  add(playerId: string, username: string, cell: Axial, slot = 0): void {
    const existing = this.markers.get(playerId);
    if (existing) {
      // **Ponowne wejście na mapę kasuje to, co pionek robił przedtem.** Tor
      // sprzed wsiadania i zaplanowane zniknięcie dotyczą kogoś, kogo w tym
      // czasie nie było; zostawione, zjadłyby to wejście — `moveTo` przy
      // sunącym torze nie robi nic, a `leaving` zdjęłoby pionek zaraz po tym,
      // jak wrócił.
      existing.glide = undefined;
      existing.leaving = false;
      existing.elapsed = existing.duration;
      this.moveTo(playerId, cell, slot);
      return;
    }

    const color = playerId === this.self ? SELF_COLOR : colorFor(username);
    const mesh = new THREE.Group();
    const body = this.body(color);
    mesh.add(body);
    // Postawiony tak, jakby był sam; `restack` zaraz rozsunie wszystkich, jeśli
    // sam nie jest — i zrobi to jedną regułą dla nowego pionka i dla tych,
    // którzy już tam stali.
    const at = this.place(cell, slot, true, 0);
    mesh.position.copy(at);

    this.object.add(mesh);
    this.markers.set(playerId, {
      username,
      cell,
      slot,
      mesh,
      color,
      body,
      target: at.clone(),
      from: at.clone(),
      control: at.clone(),
      enter: new THREE.Vector3(),
      duration: SHORTEST_STEP,
      elapsed: SHORTEST_STEP,
      leaving: false,
    });
    this.restack(cell);
  }

  /**
   * Prowadź ten pionek **całą trasą**, zamiast od kroku do kroku.
   *
   * Czasy kroków liczy wołający, bo to on zna regułę: pieszo z gruntu,
   * a statek jedną liczbą. Pusta lista zdejmuje sunięcie.
   */
  follow(playerId: string, cells: Axial[], seconds: number[]): void {
    const marker = this.markers.get(playerId);
    if (!marker) return;

    if (cells.length === 0) {
      marker.glide = undefined;
      return;
    }

    const glide = marker.glide ?? new Glide();
    glide.follow(
      marker.mesh.position.clone().setY(0),
      cells.map((cell) => {
        const { x, z } = axialToWorld(cell.q, cell.r);
        return new THREE.Vector3(x, 0, z);
      }),
      seconds
    );
    marker.glide = glide;
    // Nowa trasa kasuje pomiar starej: dwie liczby z dwóch różnych tras nie są
    // porównaniem niczego.
    marker.travel = undefined;
  }

  /** Ile tor tej bryły miał trwać i ile trwał — do zestawienia z serwerem. */
  timing(playerId: string): { planned: number; took: number } | undefined {
    return this.markers.get(playerId)?.travel;
  }

  /**
   * Przestań sunąć — marsz się skończył albo został odwołany.
   *
   * **Zostaw znacznik tam, gdzie pionek naprawdę jest.** `cell` jest w trakcie
   * sunięcia **celem trasy**, a niedokończone przejście — tym sprzed
   * wyruszenia: zgaszony tor bez tego rzucał pionkiem najpierw na punkt startu,
   * a potem na koniec trasy.
   */
  unfollow(playerId: string): void {
    const marker = this.markers.get(playerId);
    if (!marker?.glide) return;
    marker.glide = undefined;

    // **Dociągnięcie do serwera, a nie zamrożenie obrazu.** Marsz przerwany
    // w połowie zostawiał pionek tam, gdzie akurat był narysowany — a to bywa
    // o pole obok tego, co wie serwer.
    const spot = marker.mesh.position;
    const target = this.place(
      marker.cell,
      marker.slot,
      this.standingOn(marker.cell).length <= 1,
      spot.y - HEIGHT / 2
    );
    marker.from.copy(spot);
    marker.control.copy(spot).lerp(target, 0.5);
    marker.target.copy(target);
    marker.duration = SHORTEST_STEP * 2;
    marker.elapsed = 0;
  }

  /** Zatrzymaj pionek tam, gdzie jest: przejście zamknięte na miejscu. */
  private standStill(marker: Marker): void {
    const spot = marker.mesh.position;
    marker.from.copy(spot);
    marker.control.copy(spot);
    marker.target.copy(spot);
    marker.elapsed = marker.duration;
  }

  moveTo(playerId: string, cell: Axial, slot = 0): void {
    const marker = this.markers.get(playerId);
    if (!marker) return;

    // **Wiadomość o kroku jest potwierdzeniem, nie zezwoleniem — i nic z nią
    // nie robimy.** Pionek sunący własną trasą wyprzedza serwer z natury rzeczy:
    // rusza od razu, a tamten na najbliższym takcie i po drodze sieci. Pierwsza
    // wersja porównywała tu pola i przy rozjeździe zdejmowała sunięcie — czyli
    // **sama produkowała skok**, który miała łapać, i to tym częściej, im dłuższa
    // trasa. Ile naprawdę wynosi ta różnica, mówi pomiar na końcu trasy (patrz
    // `Glide` i „Pomiar rozjazdu" w `client.md`), a nie zgadywanie w pół drogi.
    if (marker.glide?.running) {
      // **Obrazu nie ruszamy, ale pole zapamiętujemy.** `cell` znaczy „gdzie
      // serwer stawia ten pionek": tor wie, gdzie go **rysuje**, a te dwie
      // rzeczy różnią się w trakcie marszu o krok. Bez tego zgaszony tor
      // zostawiał pionek o pole obok tego, co wie serwer.
      marker.cell = cell;
      marker.slot = slot;
      return;
    }

    const jumped = distance(marker.cell, cell) > SNAP_DISTANCE;
    const left = marker.cell;

    // Ruch pionka, który miał zniknąć, znaczy, że jednak idzie dalej.
    marker.leaving = false;
    marker.cell = cell;
    marker.slot = slot;
    marker.target.copy(
      this.place(
        cell,
        slot,
        this.standingOn(cell).length <= 1,
        marker.mesh.position.y - HEIGHT / 2
      )
    );

    // **Czas, a nie prędkość.** Przejście trwa dokładnie tyle, ile krok trwa na
    // serwerze — tą samą funkcją, którą serwer go liczy, więc z premią za drogę
    // i karami za las i kamień. Stała prędkość, którą to miało wcześniej,
    // dojeżdżała na miejsce przed czasem i czekała na następną wiadomość: stąd
    // brał się ruch skokowy, najbardziej widoczny na trakcie, gdzie serwer
    // jest o piątą część szybszy od tego, co pionek zakładał.
    // **Kierunek wyjścia bierze się z kroku, który się właśnie kończy**, a nie
    // z klatki, w której akurat dobiegł końca: następna wiadomość potrafi
    // przyjść przed tą klatką i wtedy nie byłoby czego przepisać. Styczna na
    // końcu łuku to `target − control`.
    if (marker.elapsed > 0) {
      const exit = FACING.copy(marker.target).sub(marker.control);
      if (exit.lengthSq() > 1e-8) marker.enter.copy(exit).normalize();
    }

    marker.from.copy(marker.mesh.position);
    // **Łuk zamiast łamanej.** Punkt sterujący leży na kierunku, którym pionek
    // tu wszedł, więc krok wychodzi tam, gdzie poprzedni przyszedł — obrót
    // i tor zmieniają się ciągle, zamiast łamać się o 60° na każdym środku
    // pola. Przodem do kierunku marszu obraca się odtąd co klatkę, ze stycznej
    // łuku; rozsunięcie na stanowiska w obrębie tego samego pola krokiem nie
    // jest i łuku nie dostaje.
    if (left.q !== cell.q || left.r !== cell.r) {
      bendOf(marker.from, marker.target, marker.enter, marker.control);
    } else {
      marker.control.copy(marker.from).lerp(marker.target, 0.5);
    }
    marker.duration = Math.max(
      SHORTEST_STEP,
      crossingSeconds(this.world.get(axialKey(cell.q, cell.r)))
    );
    marker.elapsed = 0;

    if (jumped) {
      marker.mesh.position.copy(marker.target);
      marker.from.copy(marker.target);
      marker.elapsed = marker.duration;
    }

    // Oba pola, nie tylko to nowe: krok zmienia skład i tam, skąd się wyszło —
    // ostatni, który został, wraca na środek.
    if (left.q !== cell.q || left.r !== cell.r) this.restack(left);
    this.restack(cell);
  }

  /**
   * Pionek schodzi z mapy: niech **dojdzie** i dopiero wtedy zniknie.
   *
   * Zejście jest u serwera chwilą — położył się spać, wyszedł z zasięgu, został
   * rozłączony — a tutaj jest końcem ruchu. Marker zostaje więc do końca
   * przejścia i gaśnie w `update`; gdy nic się nie dzieje, gaśnie natychmiast,
   * bo nie ma czego dokańczać. Reszta pola przestawia się od razu: ten, kto
   * odchodzi, już się nie liczy do rozkładu.
   */
  remove(playerId: string): void {
    const marker = this.markers.get(playerId);
    if (!marker) return;

    // Sunący tor nie ma czego dokańczać: kto wsiadł na statek, zszedł z mapy
    // i trasa przestała go dotyczyć.
    if (marker.elapsed < marker.duration && !marker.glide?.running) {
      marker.leaving = true;
      this.restack(marker.cell);
      return;
    }

    this.drop(playerId, marker);
  }

  private drop(playerId: string, marker: Marker): void {
    this.object.remove(marker.mesh);
    this.release(marker.mesh);
    this.markers.delete(playerId);
    this.restack(marker.cell);
  }

  /**
   * Zdejmij wszystkie, i to od razu.
   *
   * Nie przez `remove`: sprzątanie po zmianie świata albo rozłączeniu nie jest
   * dojściem donikąd i nie ma na co czekać — pionki, których dokańczanie drogi
   * dotyczy, i tak przestały istnieć.
   */
  clear(): void {
    for (const [playerId, marker] of [...this.markers]) {
      this.drop(playerId, marker);
    }
  }

  /** Where the pawn of a player is, if there is one. */
  cellOf(playerId: string): Axial | null {
    return this.markers.get(playerId)?.cell ?? null;
  }

  /**
   * Kto stoi na tym polu, i jak się nazywa.
   *
   * **Pytanie odwrotne do `cellOf`** i po nie sięga kursor: kliknięcie zna pole,
   * a nie gracza. Pętla po znacznikach zamiast drugiej mapy „pole → gracz",
   * bo pionków w zasięgu jest kilkadziesiąt, a druga mapa byłaby drugim
   * miejscem do zaktualizowania przy każdym kroku — czyli tym, które kiedyś
   * pokaże kogoś, kto już poszedł.
   *
   * **Własny pionek jest pomijany.** Markery trzymają go razem z pozostałymi
   * (po to, żeby miał kolor własny), ale to jest pytanie „kogo tu widzę", a nie
   * „co stoi na polu" — a sam ze sobą nie porozmawiam.
   */
  whoIsAt(cell: Axial, point?: THREE.Vector3): { id: string; username: string } | null {
    let closest: { id: string; username: string } | null = null;
    let nearest = Infinity;

    for (const [id, marker] of this.markers) {
      if (id === this.self) continue;
      // Pionek w drodze do zniknięcia jest już poza mapą po stronie serwera:
      // zagadać go nie sposób, więc nie ma go tu po co pokazywać.
      if (marker.leaving) continue;
      if (marker.cell.q !== cell.q || marker.cell.r !== cell.r) continue;

      // Bez punktu — pierwszy z brzegu, jak było. Z punktem — ten, w którego
      // kliknięto: odkąd na polu stoi sześcioro, „ktoś stąd" byłoby loterią,
      // a menu otwarte nad kimś innym niż wskazany jest gorsze niż brak menu.
      if (!point) return { id, username: marker.username };

      const away = marker.mesh.position.distanceToSquared(point);
      if (away < nearest) {
        nearest = away;
        closest = { id, username: marker.username };
      }
    }

    return closest;
  }

  /**
   * Re-read every height from the terrain.
   *
   * Called when chunks arrive: a pawn placed before its ground existed is
   * floating at whatever height it inherited, and this is what puts it down.
   */
  refresh(): void {
    for (const marker of this.markers.values()) {
      // **Sunącego nie przestawia się.** `cell` jest u niego **celem trasy**,
      // nie polem, na którym stoi — a teren dojeżdża w trakcie marszu, więc
      // przeliczenie wysokości przerzucałoby pionek na koniec trasy. Wysokość
      // i tak czyta się co klatkę, z terenu pod bryłą.
      if (marker.glide?.running) continue;
      if (this.groundY(marker.cell) === null) continue;

      // Przez `place`, a nie przez samą wysokość pola: pionek na stanowisku
      // stoi na nachyleniu między środkiem a krawędzią, więc jego wysokość jest
      // funkcją miejsca, a nie tylko pola.
      const standing = this.place(
        marker.cell,
        marker.slot,
        this.standingOn(marker.cell).length <= 1,
        marker.mesh.position.y - HEIGHT / 2
      );

      // Oba końce przejścia, nie tylko cel: teren, który właśnie dojechał,
      // podnosi też punkt, z którego pionek wyszedł — inaczej wjeżdżałby na
      // miejsce od dołu, przez ziemię.
      marker.target.setY(standing.y);
      marker.from.setY(standing.y);
    }
  }

  update(delta: number): void {
    for (const [id, marker] of [...this.markers]) {
      // **Sunięcie po trasie pyta się pierwsze** — i to była pomyłka warta
      // zapisania. Stało niżej, pod gałęzią „krok się skończył", a ta zachodzi
      // przez większość trasy: pionek dostaje tor, stojąc, więc `elapsed` jest
      // od początku równe `duration`. Co klatkę przystawiało to pionek do
      // `target`, czyli do **ostatniego potwierdzonego pola** — a `moveTo`
      // podczas sunięcia nic nie przestawia, więc było nim pole **startowe**.
      // Objaw: postać stała w punkcie wyjścia przez cały rejs, razem ze swoją
      // etykietą.
      if (marker.glide?.running) {
        // Zniknięcie w trakcie sunięcia nie ma na co czekać: trasa przestała
        // dotyczyć kogoś, kogo nie ma na mapie — tak jest przy wsiadaniu.
        if (marker.leaving) {
          this.drop(id, marker);
          continue;
        }
        if (marker.glide.advance(delta)) {
          marker.glide.point(marker.mesh.position);
          const heading = marker.glide.tangent(FACING);
          if (heading.lengthSq() > 1e-8) {
            marker.mesh.rotation.y = Math.atan2(heading.x, heading.z) + MODEL_FACING;
          }
          const surface = walkY(this.world, marker.mesh.position.x, marker.mesh.position.z);
          if (surface !== null) marker.mesh.position.y = surface + HEIGHT / 2;
          continue;
        }
        // Trasa się skończyła: dalej już zwykłym wygładzaniem, od tego miejsca
        // — łącznie z polem, bo `cell` było celem trasy, a nie polem pod bryłą.
        marker.travel = marker.glide.timing;
        marker.glide = undefined;
        this.standStill(marker);
        continue;
      }

      if (marker.elapsed >= marker.duration) {
        marker.mesh.position.copy(marker.target);
        // Doszedł i miał zniknąć — teraz jest na to pora.
        if (marker.leaving) this.drop(id, marker);
        continue;
      }

      // Liniowo w czasie: ułamek przebytej drogi to ułamek minionego czasu
      // przejścia. Interpolacja liczona **od punktu wyjścia**, a nie od miejsca,
      // w którym pionek jest teraz — inaczej każdy krok byłby ułamkiem tego, co
      // zostało, czyli ruchem zwalniającym przy każdym polu.
      marker.elapsed += delta;
      const part = Math.min(1, marker.elapsed / marker.duration);
      alongStep(marker.from, marker.control, marker.target, part, marker.mesh.position);

      // Przodem tam, gdzie naprawdę idzie w tej chwili — ze stycznej łuku,
      // a nie ze środka pola, do którego zmierza.
      const heading = headingOnStep(
        marker.from,
        marker.control,
        marker.target,
        part,
        FACING
      );
      if (heading.lengthSq() > 1e-8) {
        marker.mesh.rotation.y = Math.atan2(heading.x, heading.z) + MODEL_FACING;
      }
      // **Po terenie, nie po cięciwie.** Prosta między środkami pól przecina
      // powierzchnię, która schodzi do krawędzi i dopiero stamtąd do sąsiada —
      // pionek raz wisiał, raz się zapadał. Wysokość bierze się więc z terenu
      // w tym punkcie; bez wczytanego pola zostaje interpolacja.
      // Na moście — po pokładzie.
      const ground = walkY(this.world, marker.mesh.position.x, marker.mesh.position.z);
      if (ground !== null) marker.mesh.position.y = ground + HEIGHT / 2;
    }
  }

  /**
   * Gdzie bryła tego pionka jest **w tej chwili**, w jednostkach świata.
   *
   * Pyta o to kamera: ma iść za tym, co widać — czyli za torem — a nie za
   * ostatnim potwierdzonym polem, bo to drugie przychodzi skokami.
   */
  spotOf(playerId: string): THREE.Vector3 | undefined {
    return this.markers.get(playerId)?.mesh.position;
  }

  /** Everybody with a pawn, for the name tags to follow. */
  *labels(): Iterable<{ playerId: string; username: string; position: THREE.Vector3 }> {
    for (const [playerId, marker] of this.markers) {
      yield { playerId, username: marker.username, position: marker.mesh.position };
    }
  }

  dispose(): void {
    this.clear();
    this.geometry.dispose();
  }
}
